(function () {
    'use strict';

    angular
        .module('app')
        .factory('SMS', SMSFactory);

    SMSFactory.$inject = [
        '$q',
        'SMSService',
        'DocumentTemplatesService',
        'DialogService',
        'Entity',
        'EntitiesService'];

    function SMSFactory(
        $q,
        SMSService,
        DocumentTemplatesService,
        DialogService,
        Entity,
        EntitiesService) {

        /**
         * Copies an existing SMS.
         *
         * @param {number} smsId
         *
         * @returns {Promise} A promise that resolves a new {SMS} object copied from the specified smsId.
         */
        function copySMS(smsId) {
            return SMSService.get(smsId)
                .then(function (data) {
                    return new SMS({
                        To: '',
                        Message: data.Message,
                        From: [data.FromEntityId],
                        MultipleFromMode: false,
                        Tags: data.Tags
                    });
                });
        }

        /**
         * Get an existing SMS.
         *
         * @param {number} smsId
         *
         * @returns {Promise} A promise that resolves a {SMS} object for the specified smsId.
         */
        function get(smsId) {
            return SMSService.get(smsId)
                .then(function (data) {
                    return new SMS(data);
                });
        }

        /**
         * Get a new SMS.
         *
         * @param {Array|Number} An array of entity ids, or a single entity id, to be used for sending the SMS
         *
         * @returns {Promise} A promise that resolves a new {SMS} object.
         */
        function getNew(fromEntityIds) {
            var sms = new SMS();

            return validateFromEntityIds()
                .then(addEntityIdsToFrom)
                .then(setDefaultSMSTemplate)
                .then(function () {
                    if (!_.isNil(sms.data.From) && sms.data.From.length > 1) {
                        sms.data.MultipleFromMode = true;
                    } else {
                        sms.data.MultipleFromMode = false;
                    }

                    // It's technically not 'saved' data, however it is needed for the dirty check
                    sms.savedData = angular.copy(sms.data);
                    return sms;
                });

            
            /**
             * Validate that the 'From' entity ids can all be obtained from the server.
             *
             * @returns {Promise} Resolves if validation is OK, otherwise, rejects with a {string} error message.
             */
            function validateFromEntityIds() {
                if (!_.isNil(fromEntityIds)) {
                    if (!angular.isArray(fromEntityIds)) {
                        fromEntityIds = [fromEntityIds];
                    } else if (fromEntityIds.length > 1) {
                        sms.data.MultipleFromMode = true;
                    }
                }

                var entityList = [];
                var getEntityPromises = [];
                
                if (!_.isNil(fromEntityIds)) {
                    angular.forEach(fromEntityIds, function (fromEntityId) {
                        getEntityPromises.push(Entity.get(fromEntityId)
                            .then(function (data) {
                                entityList.push(data);
                            }, function () {
                                entityList.push({ Id: fromEntityId, NotFound: true });
                            }));
                    });
                }

                return $q.all(getEntityPromises)
                    .then(function () {
                        var validationFailed = false;
                        var errorMessage = '';
                        
                        angular.forEach(entityList, function (entity) {
                            if (entity.NotFound) {
                                if (errorMessage !== '') {
                                    errorMessage += '\n\r';
                                }

                                errorMessage += 'Entity not found for entity id: ' + entity.Id + '.';
                                validationFailed = true;
                            }
                        });

                        if (validationFailed) {
                            return $q.reject(errorMessage);
                        }
                    });
            }

            /**
             * Add all the entity id's to the SMS 'From' list.
             *
             * @returns {Promise} Resolved once all entries are added
             */
            function addEntityIdsToFrom() {
                if (!_.isNil(fromEntityIds)) {
                    angular.forEach(fromEntityIds, function (fromEntityId) {
                        sms.data.From.push(angular.isString(fromEntityId) ? parseInt(fromEntityId) : fromEntityId);
                    });
                }

                return $q.resolve();
            }

            /**
             * Check for a default template, and if found, set properties on the SMS.
             *
             * @returns {Promise} resolved once default template has been set.
             */
            function setDefaultSMSTemplate() {
                return sms.setDefaultSMSTemplate();
            }
        }

        /**
         * @constructor
         *
         * @property {Object} data - SMS data, refer C# class 'SMS'.
         */
        function SMS(data) {
            var vm = this;

            if (!_.isNil(data)) {
                vm.data = data;
            } else {
                vm.data = newSMS();
            }

            vm.send = send;
            vm.resend = resend;
            vm.getPreview = getPreview;
            vm.setDefaultSMSTemplate = setDefaultSMSTemplate;
            vm.setSMSTemplate = setSMSTemplate;
            vm.getValidationReport = getValidationReport;
            Object.defineProperty(vm, 'validateSMS', { get: getValidateSMS });
            Object.defineProperty(vm, 'smsPersisted', { get: getSMSPersisted });
            Object.defineProperty(vm, 'primaryEntityId', { get: getPrimaryEntityId });

            /**
             * Sends the SMS.
             *
             * @param {array} recipients - The recipients to which the SMS is to be sent. This overrides the SendTo and From properties of the SMS. Refer to the C# class 'SMSSendReport'.
             *
             * @returns {Promise} Promise that will be resolved to a response object when the send request succeeds or fails.
             */
            function send(recipients) {
                var smsData = {
                    Message: vm.data.Message,
                    SMSAddressingList: [],
                    Tags: vm.data.Tags,
                    SourceTemplateIds: vm.data.SourceTemplateIds
                };

                // If the recipients has not been supplied, create the send request data using the SMS
                if (_.isNil(recipients)) {
                    angular.forEach(vm.data.From, function (fromEntityId) {
                        smsData.SMSAddressingList.push({
                            FromEntityId: fromEntityId,
                            To: vm.data.SendTo
                        });
                    });
                } else {
                    // If the recipients have been supplied, create the send request using the recipients data
                    angular.forEach(recipients, function (recipient) {
                        var existingSMSAddressing = _.find(smsData.SMSAddressingList, function (smsAddressing) {
                            return smsAddressing.FromEntityId === recipient.FromEntityId;
                        });

                        if (_.isNil(existingSMSAddressing)) {
                            smsData.SMSAddressingList.push({
                                FromEntityId: recipient.FromEntityId,
                                To: [recipient.To]
                            });
                        } else {
                            existingSMSAddressing.To.push(recipient.To);
                        }
                    });
                }

                return SMSService.sendSMS(smsData);
            }

            /**
             * Resends the SMS.
             *
             * @returns {Promise} Promise that will be resolved to a response object when the resend request succeeds or fails.
             */
            function resend() {
                var promise = {};

                if (vm.smsPersisted) {
                    promise = SMSService.resendSMS(vm.data.Id);
                } else {
                    promise = $q.reject();
                }
                
                return promise;
            }

            /**
             * Generates a list of validation errors for the SMS
             *
             * @returns {array} List of validation errors for the SMS
             */
            function getValidateSMS() {
                var result = [];

                if (_.isNil(vm.data.SendTo) || vm.data.SendTo.length  === 0) {
                    result.push('SMS To is required');
                }

                if (_.isNil(vm.data.Message) || vm.data.Message === '') {
                    result.push('SMS Message is required');
                }

                return result;
            }

            /**
             * Gets a 'preview' for the SMS, which is an SMS object with all of the send pre-processing conducted on the SMS.
             *
             * @param {number} entityId - Preview should use the supplied entity for resolving merge fields. If not supplied, the first 'From entity' will be used. Optional.
             * @returns {Promise} A promise which will resolve with an SMS object which can be used for showing a preview of the SMS with send pre-processing conducted.
             */
            function getPreview(entityId) {
                return SMSService.getPreview(vm.data, entityId)
                    .then(getPreviewOk);

                function getPreviewOk(data) {
                    return new SMS(data);
                }
            }

            /**
             * Gets a validation report for the SMS, which is used to show the pre-send dialog information for Multi Mode SMS.
             *
             * @returns {Promise} A promise which will resolve with a validation report object.
             */
            function getValidationReport() {
                return SMSService.getValidationReport({
                    Message: vm.data.Message,
                    SendTo: vm.data.SendTo,
                    From: vm.data.From
                });
            }

            /**
             * Set SMS properties according to the default template.
             *
             * @returns {Promise} Resolves once the default SMS template has been loaded and use to set SMS properties.
             */
            function setDefaultSMSTemplate() {
                if (!_.isNil(vm.primaryEntityId)) {
                    return EntitiesService.getDefaultSMSTemplate(vm.primaryEntityId)
                        .then(function (smsTemplate) {
                            if (!_.isNil(smsTemplate)) {
                                return setSMSTemplate(smsTemplate);
                            } else {
                                return $q.resolve();
                            }
                        });
                } else {
                    return $q.resolve();
                }
            }

            /**
             * Retrieves the document template files referenced by the SMS template and uses these to populate the corresponding field on the SMS.
             *
             * @returns {Promise} A promise that resolves once the SMS template has been loaded and used to set the SMS Message.
             */
            function setSMSTemplate(smsTemplate) {
                var setSMSTemplatePromises = [];
                var message = null;
                var sourceTemplateIds = [];

                if (!_.isNil(smsTemplate.Id) && smsTemplate.Id !== '') {
                    sourceTemplateIds.push(smsTemplate.Id);
                }

                if (!_.isNil(smsTemplate.To) && angular.isArray(smsTemplate.To) && smsTemplate.To.length > 0) {
                    vm.data.SendTo = angular.copy(smsTemplate.To);
                }

                if (!_.isNil(smsTemplate.Message) && smsTemplate.Message !== '') {
                    sourceTemplateIds.push(smsTemplate.Message);
                    setSMSTemplatePromises.push(DocumentTemplatesService.getDocumentFile(smsTemplate.Message).then(getMessageFileOk));
                }

                function getMessageFileOk(response) {
                    message = String.fromCharCode.apply(null, new Uint8Array(response.data));
                }

                return $q.all(setSMSTemplatePromises)
                    .then(function () {
                        // The values should only be set on the email data if all of the document templates were successfully retrieved.
                        if (!_.isNil(message)) {
                            vm.data.Message = message;
                        }

                        vm.data.SourceTemplateIds = sourceTemplateIds;
                    });
            }

            /**
             * Determine if the SMS has been persisted
             *
             * @returns {bool} Has the SMS been persisted
             */
            function getSMSPersisted() {
                var result = false;

                if (!_.isNil(vm.data.Id)) {
                    result = true;
                }

                return result;
            }

            /**
             * A SMS is associated with one or more entities, used for field merging.
             * The "primary" entity ID is just the first of those entities.
             *
             * @returns {number} Primary entity id for the SMS, may be null.
             */
            function getPrimaryEntityId() {
                if (vm.data.From.length > 0 && _.isNumber(vm.data.From[0])) {
                    return vm.data.From[0];
                } else {
                    return null;
                }
            }

            function newSMS() {
                return {
                    To: '',
                    Message: '',
                    From: [],
                    Tags: [],
                    SourceTemplateIds: []
                };
            }
        }

        SMS.prototype = {};
        SMS.prototype.constructor = SMS;

        SMS.get = get;
        SMS.getNew = getNew;
        SMS.copySMS = copySMS;

        return SMS;
    }
})();